import { useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"
import { IconCopyFill18 } from "nucleo-ui-fill-18"
import { IconInboxOutline18 } from "nucleo-ui-outline-18"

import type { PainLevel } from "@productlane/db/browser"
import type {
	UpdateThreadArgs,
	UpdateThreadBulkArgs,
} from "@productlane/zero/types"
import { SnoozeIcon } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { CopyOption } from "./thread-menu-model"
import type { MenuThread } from "./thread-menu-submenus"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { mutateOk, useZero } from "@/lib/zero"
import { useInboxAnchorReset } from "@/screens/app/inbox/inbox-anchor-reset"
import { formatSnoozeTime } from "@/screens/app/threads/components/center-content/snooze-options"
import { useThreadsMultiSelectState } from "@/screens/app/threads/multi-select/threads-multi-select-state"
import {
	THREAD_MENU_COPY as copy,
	isDoneState,
	threadCount,
	threadPath,
} from "./thread-menu-model"

const iso = (ms: number | null | undefined) =>
	ms ? new Date(ms).toISOString() : null

export type ThreadMenuActions = ReturnType<typeof useThreadMenuActions>
export type BulkThreadMenuActions = ReturnType<typeof useBulkThreadMenuActions>

// Each push runs one transaction with an activity row and an Inngest event per thread.
const MAX_IDS_PER_CALL = 500

function chunks<T>(items: readonly T[]): T[][] {
	const out: T[][] = []
	for (let i = 0; i < items.length; i += MAX_IDS_PER_CALL)
		out.push(items.slice(i, i + MAX_IDS_PER_CALL))
	return out
}

/** The right-click menu over a multi-selection: the same bulk changes as the selection bar. */
export function useBulkThreadMenuActions() {
	const zero = useZero()
	const utils = api.useUtils()
	const clearThreadIds = useThreadsMultiSelectState((s) => s.clearThreadIds)
	const resetInboxAnchor = useInboxAnchorReset((s) => s.resetInboxAnchor)

	return useMemo(() => {
		const fail = () => toast.error({ title: copy.bulkFailed })
		const run = async (action: string, work: () => Promise<unknown>) => {
			analytics.track("thread-context-menu", { action, bulk: true })
			try {
				await work()
				return true
			} catch {
				fail()
				return false
			}
		}
		const updateBulk = (
			ids: readonly string[],
			change: Omit<UpdateThreadBulkArgs, "ids">,
		) =>
			Promise.all(
				chunks(ids).map((batch) =>
					mutateOk(
						zero.mutate(mutators.threads.updateBulk({ ...change, ids: batch })),
					),
				),
			)
		const updateEach = (
			ids: readonly string[],
			change: Omit<UpdateThreadArgs, "id">,
		) =>
			Promise.all(
				ids.map((id) =>
					mutateOk(zero.mutate(mutators.threads.update({ id, ...change }))),
				),
			)
		// State changes move rows out of the list, so the selection goes with them.
		const leaveSelection = () => {
			resetInboxAnchor()
			setTimeout(clearThreadIds, 0)
		}

		return {
			async setDone(ids: readonly string[], done: boolean) {
				const ok = await run(done ? "mark-done" : "move-to-inbox", () =>
					updateBulk(ids, {
						state: done ? "PROCESSED" : "NEW",
						lastStateChangeAtIso: new Date().toISOString(),
					}),
				)
				if (!ok) return
				leaveSelection()
				toast.success({
					...(done ? {} : { icon: IconInboxOutline18 }),
					title: done
						? `Marked ${threadCount(ids.length)} as done.`
						: `Moved ${threadCount(ids.length)} to Inbox.`,
				})
			},

			async snooze(ids: readonly string[], value: Date) {
				const ok = await run("snooze", () =>
					updateBulk(ids, {
						state: "SNOOZED",
						snoozedUntilIso: value.toISOString(),
						lastStateChangeAtIso: new Date().toISOString(),
					}),
				)
				if (!ok) return
				leaveSelection()
				toast.success({
					icon: SnoozeIcon,
					title: `Snoozed ${threadCount(ids.length)} until ${formatSnoozeTime(value)}.`,
				})
			},

			async unsnooze(ids: readonly string[]) {
				const ok = await run("unsnooze", () =>
					updateBulk(ids, {
						state: "UNSNOOZED",
						snoozedUntilIso: null,
						lastStateChangeAtIso: new Date().toISOString(),
					}),
				)
				if (!ok) return
				leaveSelection()
				toast.success({
					icon: SnoozeIcon,
					title: `Unsnoozed ${threadCount(ids.length)}.`,
				})
			},

			async setRead(ids: readonly string[], isRead: boolean) {
				await run(isRead ? "mark-read" : "mark-unread", () =>
					Promise.all(
						chunks(ids).map((batch) =>
							mutateOk(
								zero.mutate(
									mutators.threads.setReadBulk({ ids: batch, isRead }),
								),
							),
						),
					),
				)
			},

			async setPriority(ids: readonly string[], painLevel: PainLevel) {
				await run("priority", () => updateEach(ids, { painLevel }))
			},

			async assign(ids: readonly string[], assigneeId: string | null) {
				await run("assign", () => updateEach(ids, { assigneeId }))
			},

			async remove(ids: readonly string[]) {
				clearThreadIds()
				resetInboxAnchor()
				const ok = await run("delete", () =>
					Promise.all(
						chunks(ids).map((batch) =>
							mutateOk(
								zero.mutate(mutators.threads.deleteBulk({ ids: batch })),
								"server",
							),
						),
					),
				)
				if (!ok) return
				toast.success({ title: `Deleted ${threadCount(ids.length)}.` })
				await utils.workspace.searchResults.invalidate()
			},
		}
	}, [zero, utils, clearThreadIds, resetInboxAnchor])
}

/** What the thread row menu does, with the same toasts and undo as the thread view's own controls. */
export function useThreadMenuActions() {
	const zero = useZero()
	const navigate = useNavigate()
	const utils = api.useUtils()

	return useMemo(() => {
		const fail = () => toast.error({ title: copy.failed })

		const update = (
			thread: Pick<MenuThread, "id">,
			change: Omit<UpdateThreadArgs, "id">,
			action: string,
		) => {
			analytics.track("thread-context-menu", { action })
			return mutateOk(
				zero.mutate(mutators.threads.update({ id: thread.id, ...change })),
			).catch(fail)
		}

		const restoreState = (thread: MenuThread) =>
			update(
				thread,
				{
					state: thread.state as UpdateThreadArgs["state"],
					snoozedUntilIso: iso(thread.snoozedUntil),
					lastStateChangeAtIso: iso(thread.lastStateChangeAt),
				},
				"undo",
			)

		const openThread = (thread: MenuThread) =>
			thread.title
				? {
						label: thread.title,
						callback: () => void navigate({ href: threadPath(thread.id) }),
					}
				: undefined

		return {
			update,

			toggleDone(thread: MenuThread) {
				const done = isDoneState(thread.state)
				void update(
					thread,
					{
						state: done ? "NEW" : "PROCESSED",
						lastStateChangeAtIso: new Date().toISOString(),
					},
					done ? "move-to-inbox" : "mark-done",
				)
				toast.success({
					...(done ? { icon: IconInboxOutline18 } : {}),
					title: done ? "Moved to Inbox" : "Marked done",
					descriptionAction: openThread(thread),
					onUndo: async () => {
						await restoreState(thread)
					},
				})
			},

			snooze(thread: MenuThread, value: Date) {
				void update(
					thread,
					{
						state: "SNOOZED",
						snoozedUntilIso: value.toISOString(),
						lastStateChangeAtIso: new Date().toISOString(),
					},
					"snooze",
				)
				toast.success({
					icon: SnoozeIcon,
					title: `Snoozed until ${formatSnoozeTime(value)}`,
					descriptionAction: openThread(thread),
					onUndo: async () => {
						await restoreState(thread)
					},
				})
			},

			unsnooze(thread: MenuThread) {
				void update(thread, { state: "UNSNOOZED" }, "unsnooze")
			},

			async remove(thread: MenuThread) {
				analytics.track("thread-context-menu", { action: "delete" })
				try {
					await mutateOk(
						zero.mutate(mutators.threads.delete({ id: thread.id })),
					)
				} catch {
					fail()
					return
				}
				await utils.workspace.searchResults.invalidate()
			},

			async copy(option: CopyOption) {
				analytics.track("thread-context-menu", { action: `copy-${option.key}` })
				try {
					await navigator.clipboard.writeText(option.value)
					toast.success({ title: option.copied, icon: IconCopyFill18 })
				} catch {
					toast.error({ title: copy.copyFailed })
				}
			},
		}
	}, [zero, navigate, utils])
}
